/**
 * lib/api.js -- Port 1:1 de src/js/api.js (app/frontend). Cliente fetch
 * centralizado, responsabilidad unica: hablar HTTP con el backend real
 * y regresar JSON ya parseado. Ningun otro modulo de este proyecto debe
 * usar fetch() directamente -- MISMA regla que el original (doc 06).
 *
 * Se porto COMPLETO (los 60+ endpoints), no solo los que ya tienen
 * pantalla en React todavia. Es codigo mecanico y de bajo riesgo (son
 * llamadas declarativas, no logica de negocio), y dejarlo listo de una
 * vez evita que cada fase futura (Pedidos, Dashboard, Admin...) tenga
 * que volver a este archivo. Unico cambio real respecto al original:
 * importa de `sessionStore.js` en vez de `state.js` (mismo contrato,
 * ver ese archivo).
 */
import { API_BASE_URL } from './config.js';
import { getSessionToken, getRememberToken, setSession, clearSession, markSessionExpiredNotice } from './sessionStore.js';
import { showAlert, showModuleAccessDenied, showAccountDisabled } from './ui.js';
import { MODULE_DEFS } from '../routes/moduleDefs.js';
import { isInvalidSavedSession } from './sessionRecovery.js';

const SESSION_EXPIRED_MESSAGE = 'Sesión expirada o no válida. Ingresa nuevamente.';

/** doc 22 §8 (validacion en vivo): el backend ya resuelve permisos
 * frescos en cada peticion, asi que un admin puede quitarte algo
 * mientras tu pestana sigue abierta con el permiso viejo en memoria
 * (sessionStore.permisos). Este interceptor es el "auto-sanado": cuando
 * eso pasa, el 403 trae `detail.permiso` con el codigo exacto que
 * falto -- si es el candado de un modulo entero (`modulo.X.ver`) se
 * muestra el aviso de modulo (fuerza volver a Modulos); si es una accion
 * puntual, un aviso mas chico tipo showAlert. Vive aqui (no en cada
 * componente) porque es EL unico lugar por el que pasan todas las
 * llamadas autenticadas. */
const MODULE_GATE_RE = /^modulo\.[^.]+\.ver$/;

/** doc 22 §8: variante mas severa del aviso de arriba -- cuenta
 * (usuario o proveedor) deshabilitada a mitad de sesion. No hay
 * `detail` que decidir aqui (a diferencia del 403 de permiso): el
 * `error` que manda el backend YA es el mensaje completo y correcto
 * para mostrar (distingue usuario vs proveedor del lado del backend,
 * ver `sessionService.hydrateSession`), así que se pasa tal cual. */
// Codigos del backend (accountBlock) que significan "esta cuenta ya no tiene acceso": usuario deshabilitado,
// proveedor deshabilitado o tienda inhabilitada. Todos muestran el mismo modal "Ya no tienes acceso al sistema".
const ACCOUNT_DISABLED_CODES = new Set(['AUTH_USER_DISABLED', 'AUTH_PROVIDER_DISABLED', 'AUTH_STORE_DISABLED']);

function handleAccountDisabled(message) {
  showAccountDisabled({ message });
}

function handleForbiddenPermission(detail) {
  const codigo = detail?.permiso;
  if (!codigo) return;
  if (MODULE_GATE_RE.test(codigo)) {
    const moduleDef = MODULE_DEFS.find((m) => m.permiso === codigo);
    showModuleAccessDenied({ moduleTitle: moduleDef?.title || 'este módulo' });
    return;
  }
  showAlert({
    type: 'warning',
    title: 'No tienes permiso para esta acción',
    message: 'Tu perfil ya no incluye el permiso necesario para hacer esto. Si crees que es un error, contacta a un administrador.',
    okLabel: 'Entendido',
  });
}

async function rawRequest(path, { method = 'GET', body, auth = false, query } = {}) {
  const url = new URL(API_BASE_URL + path, window.location.origin);
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
    });
  }

  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = typeof auth === 'string' ? auth : getSessionToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(url.toString(), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    return { ok: false, error: 'No se pudo conectar con el servidor. Revisa tu conexion e intenta de nuevo.' };
  }

  let json;
  try {
    json = await res.json();
  } catch {
    json = { ok: false, error: `Respuesta inválida del servidor (HTTP ${res.status}).` };
  }
  return json;
}

let resumeInFlight = null;

async function resumeSessionOnce() {
  if (!resumeInFlight) {
    resumeInFlight = (async () => {
      const rememberToken = getRememberToken();
      if (!rememberToken) return { ok: false, code: 'AUTH_SAVED_SESSION_INVALID' };
      const result = await rawRequest('/api/auth/resume', { method: 'POST', body: { rememberToken } });
      if (result.ok) setSession(result);
      return result;
    })().finally(() => { resumeInFlight = null; });
  }
  return resumeInFlight;
}

async function request(path, options = {}, retried = false) {
  const json = await rawRequest(path, options);
  if (options.auth && json?.ok === false) {
    if (json.code === 'AUTH_FORBIDDEN_PERMISSION') handleForbiddenPermission(json.detail);
    else if (ACCOUNT_DISABLED_CODES.has(json.code)) handleAccountDisabled(json.error);
  }
  const expired = options.auth && json?.ok === false && (json.code === 'SESSION_EXPIRED' || json.error === SESSION_EXPIRED_MESSAGE);
  if (!expired || retried) return json;

  // Un token explícito ajeno a la sesión interna pertenece a otro portal.
  if (typeof options.auth === 'string' && options.auth !== getSessionToken()) return json;
  const resumed = await resumeSessionOnce();
  if (resumed.ok) {
    const retryOptions = typeof options.auth === 'string' ? { ...options, auth: true } : options;
    return request(path, retryOptions, true);
  }
  if (!isInvalidSavedSession(resumed)) return resumed;

  clearSession();
  markSessionExpiredNotice();
  window.location.reload();
  return json;
}

async function postForBinary(path, payload, fallbackName) {
  const token = getSessionToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(API_BASE_URL + path, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    return { ok: false, error: 'No se pudo conectar con el servidor. Revisa tu conexion e intenta de nuevo.' };
  }

  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: `Error al exportar (HTTP ${res.status}).` }));
    return { ok: false, error: json.error || `Error al exportar (HTTP ${res.status}).` };
  }

  const filename = res.headers.get('X-Filename') || fallbackName;
  const blob = await res.blob();
  return { ok: true, blob, filename };
}

const exportRecords = (payload) => postForBinary('/api/reports/records/export', payload, 'pedidos.xlsx');
const exportDriverReports = (payload) => postForBinary('/api/driver-reports/export', payload, 'reporte-drivers.xlsx');

async function downloadBinary(path, fallbackName, errorLabel) {
  const token = getSessionToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(API_BASE_URL + path, { headers });
  } catch {
    return { ok: false, error: 'No se pudo conectar con el servidor. Revisa tu conexion e intenta de nuevo.' };
  }
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    return { ok: false, error: json.error || `${errorLabel} (HTTP ${res.status}).` };
  }
  const filename = res.headers.get('X-Filename') || fallbackName;
  const blob = await res.blob();
  return { ok: true, blob, filename };
}

/** Envia un archivo de tiendas a `path` (importar o vista previa). Sin modo: importar nunca desactiva. */
async function postStoresFile(path, file) {
  const token = getSessionToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const body = new FormData();
  body.append('file', file);

  let res;
  try {
    res = await fetch(API_BASE_URL + path, { method: 'POST', headers, body });
  } catch {
    return { ok: false, error: 'No se pudo conectar con el servidor. Revisa tu conexion e intenta de nuevo.' };
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok && !json.error) return { ok: false, error: `Error al importar (HTTP ${res.status}).` };
  return json;
}

const importStoresFile = (file) => postStoresFile('/api/admin/stores/import-file', file);
const previewStoresFile = (file) => postStoresFile('/api/admin/stores/import-preview', file);

const getStoresTemplate = () =>
  downloadBinary('/api/admin/stores/template', 'plantilla-tiendas.xlsx', 'Error al descargar la plantilla');

/** `estatus` (todas | habilitadas | inhabilitadas) solo lo elige el administrador; el servidor ignora/rechaza lo que no le toca a cada perfil. */
const exportStores = (estatus) =>
  downloadBinary(`/api/admin/stores/export${estatus ? `?estatus=${encodeURIComponent(estatus)}` : ''}`, 'catalogo-tiendas.xlsx', 'Error al exportar las tiendas');

// React StrictMode puede montar dos veces el arranque; no rotar dos veces
// la misma credencial mientras ambas llamadas están en curso.
const pendingResumes = new Map();
function resume(rememberToken, portal = 'INTERNO') {
  const key = `${portal}:${rememberToken}`;
  if (!pendingResumes.has(key)) {
    const pending = request('/api/auth/resume', { method: 'POST', body: { rememberToken, portal } })
      .finally(() => pendingResumes.delete(key));
    pendingResumes.set(key, pending);
  }
  return pendingResumes.get(key);
}

export const api = {
  // Auth
  register: (payload) => request('/api/auth/register', { method: 'POST', body: payload }),
  login: (usuario, password, portal = 'INTERNO') => request('/api/auth/login', { method: 'POST', body: { usuario, password, portal } }),
  resume,
  logout: (token) => request('/api/auth/logout', { method: 'POST', auth: token || true }),
  setMyTheme: (token, theme) => request('/api/auth/my-theme', { method: 'POST', body: { theme }, auth: token || true }),
  setMyLoginTheme: (token, theme) => request('/api/auth/my-login-theme', { method: 'POST', body: { theme }, auth: token || true }),
  changeMyPassword: (payload, token) => request('/api/auth/change-password', { method: 'POST', body: payload, auth: token || true }),

  getMyTelegram: () => request('/api/auth/my-telegram', { auth: true }),
  setMyTelegram: (payload) => request('/api/auth/my-telegram', { method: 'POST', body: payload, auth: true }),
  removeMyTelegram: () => request('/api/auth/my-telegram/remove', { method: 'POST', auth: true }),
  setMyTelegramGroup: (payload) => request('/api/auth/my-telegram-group', { method: 'POST', body: payload, auth: true }),
  removeMyTelegramGroup: () => request('/api/auth/my-telegram-group/remove', { method: 'POST', auth: true }),

  // Catalogs
  getCatalogs: () => request('/api/catalog', { auth: true }),
  getStores: (accesoFormato, uso = 'alta') => request('/api/catalog/stores', { query: { accesoFormato, uso }, auth: true }),
  getCatalogVersion: () => request('/api/catalog/version', { auth: true }),
  getProveedores: () => request('/api/catalog/proveedores', { auth: true }),
  getTheme: () => request('/api/catalog/theme'),
  getProveedorTheme: () => request('/api/catalog/theme-proveedor'),

  // Records
  listRecords: (payload) => request('/api/records/list', { method: 'POST', body: payload, auth: true }),
  getRecordDetail: (id, payload) => request(`/api/records/detail/${encodeURIComponent(id)}`, { method: 'POST', body: payload, auth: true }),
  createRecords: (payload) => request('/api/records/create', { method: 'POST', body: payload, auth: true }),
  reprogramRecords: (payload) => request('/api/records/reprogram', { method: 'POST', body: payload, auth: true }),
  requestReturn: (originId, payload) => request(`/api/records/return/${originId}`, { method: 'POST', body: payload, auth: true }),
  requestExchange: (originId, payload) => request(`/api/records/exchange/${originId}`, { method: 'POST', body: payload, auth: true }),
  cancelRecords: (payload) => request('/api/records/cancel', { method: 'POST', body: payload, auth: true }),
  approveRecords: (payload) => request('/api/records/approve', { method: 'POST', body: payload, auth: true }),
  rejectRecords: (payload) => request('/api/records/reject', { method: 'POST', body: payload, auth: true }),
  confirmDelivery: (payload) => request('/api/records/confirm-delivery', { method: 'POST', body: payload, auth: true }),
  confirmService: (payload) => request('/api/records/confirm-service', { method: 'POST', body: payload, auth: true }),
  reassignProvider: (payload) => request('/api/records/reassign-provider', { method: 'POST', body: payload, auth: true }),
  // doc 29: Devuelto, linea de tiempo, incidencias y portal Proveedor.
  returnToStore: (payload) => request('/api/records/return-to-store', { method: 'POST', body: payload, auth: true }),
  getRecordTimeline: (id, payload) => request(`/api/records/timeline/${encodeURIComponent(id)}`, { method: 'POST', body: payload, auth: true }),
  listIncidencias: (payload) => request('/api/incidencias/list', { method: 'POST', body: payload, auth: true }),
  resolveIncidencias: (payload) => request('/api/incidencias/resolve', { method: 'POST', body: payload, auth: true }),
  registerFalseTripLastMile: (payload) => request('/api/incidencias/viajes-falso', { method: 'POST', body: payload, auth: true }),
  // Portal Proveedor: token LOCAL del portal (ver ProveedorApp.jsx), nunca el de sessionStore.
  listProviderRecords: (payload, token) => request('/api/proveedor/registros', { method: 'POST', body: payload, auth: token }),
  getProviderRecordDetail: (id, token) => request(`/api/proveedor/registros/${encodeURIComponent(id)}`, { method: 'POST', body: {}, auth: token }),
  registerFalseTripProvider: (payload, token) => request('/api/proveedor/viajes-falso', { method: 'POST', body: payload, auth: token }),
  getProviderFilterOptions: (token) => request('/api/proveedor/tiendas', { auth: token }),
  getProviderStats: (payload, token) => request('/api/proveedor/estadisticas', { method: 'POST', body: payload, auth: token }),
  getValidDates: (tiendaId) => request('/api/records/valid-dates', { query: { tiendaId }, auth: true }),
  hasStoreDeliveriesForToday: (tiendaId, ctx) => request(`/api/records/has-today/${encodeURIComponent(tiendaId)}`, { method: 'POST', body: ctx, auth: true }),

  // Dashboard
  getDashboard: (payload) => request('/api/dashboard', { method: 'POST', body: payload, auth: true }),

  // Reportes
  exportRecords,

  // Admin -- usuarios
  listUsers: () => request('/api/admin/users', { auth: true }),
  setUserProfile: (payload) => request('/api/admin/users/set-profile', { method: 'POST', body: payload, auth: true }),
  createUserAdmin: (payload) => request('/api/admin/users/create', { method: 'POST', body: payload, auth: true }),
  adminResetPassword: (userId, newPassword) => request('/api/admin/users/reset-password', { method: 'POST', body: { userId, newPassword }, auth: true }),
  forceLogoutUser: (userId) => request('/api/admin/users/force-logout', { method: 'POST', body: { userId }, auth: true }),
  getPerfiles: () => request('/api/admin/perfiles', { auth: true }),

  adminGetUserTelegram: (userId) => request(`/api/admin/users/${userId}/telegram`, { auth: true }),
  adminSetUserTelegram: (userId, chatId) => request('/api/admin/users/telegram', { method: 'POST', body: { userId, chatId }, auth: true }),
  adminRemoveUserTelegram: (userId) => request('/api/admin/users/telegram/remove', { method: 'POST', body: { userId }, auth: true }),
  adminSetUserTelegramGroup: (userId, nombre, chatId, activo) => request('/api/admin/users/telegram-group', { method: 'POST', body: { userId, nombre, chatId, activo }, auth: true }),
  adminRemoveUserTelegramGroup: (userId) => request('/api/admin/users/telegram-group/remove', { method: 'POST', body: { userId }, auth: true }),

  // Permisos
  getPermissionAdminData: () => request('/api/admin/permisos', { auth: true }),
  saveUsuarioPermisosLote: (payload) => request('/api/admin/permisos/usuario/lote', { method: 'POST', body: payload, auth: true }),
  savePerfilPermisosLote: (cambios) => request('/api/admin/permisos/perfil/lote', { method: 'POST', body: { cambios }, auth: true }),
  getGerentesAdmin: () => request('/api/admin/gerentes', { auth: true }),
  createGerente: (nombre, correo) => request('/api/admin/gerentes', { method: 'POST', body: { nombre, correo }, auth: true }),

  updateGerente: (payload) => request('/api/admin/gerentes/update', { method: 'POST', body: payload, auth: true }),
  deleteGerente: (id) => request('/api/admin/gerentes/delete', { method: 'POST', body: { id }, auth: true }),
  reactivateGerente: (id) => request('/api/admin/gerentes/reactivate', { method: 'POST', body: { id }, auth: true }),

  listSubgerentesAdmin: () => request('/api/admin/subgerentes', { auth: true }),
  saveSubgerente: (payload) => request('/api/admin/subgerentes', { method: 'POST', body: payload, auth: true }),
  deleteSubgerente: (id) => request('/api/admin/subgerentes/delete', { method: 'POST', body: { id }, auth: true }),
  reactivateSubgerente: (id) => request('/api/admin/subgerentes/reactivate', { method: 'POST', body: { id }, auth: true }),
  getAdminCatalogItems: (key) => request(`/api/admin/catalogs/${key}/items`, { auth: true }),

  getAdminConfig: () => request('/api/admin/config', { auth: true }),
  saveAdminConfigBatch: (entries) => request('/api/admin/config/batch', { method: 'POST', body: { entries }, auth: true }),

  // Plantillas de Telegram
  getPlantillasTelegram: () => request('/api/admin/plantillas-telegram', { auth: true }),
  savePlantillaTelegram: (evento, cuerpo) => request('/api/admin/plantillas-telegram', { method: 'POST', body: { evento, cuerpo }, auth: true }),
  restaurarPlantillaTelegram: (evento) => request('/api/admin/plantillas-telegram/restaurar', { method: 'POST', body: { evento }, auth: true }),

  createCatalogItem: (key, nombre) => request(`/api/admin/catalogs/${key}/items`, { method: 'POST', body: { nombre }, auth: true }),
  updateCatalogItem: (key, id, nombre) => request(`/api/admin/catalogs/${key}/items/update`, { method: 'POST', body: { id, nombre }, auth: true }),
  deleteCatalogItem: (key, id) => request(`/api/admin/catalogs/${key}/items/delete`, { method: 'POST', body: { id }, auth: true }),
  reactivateCatalogItem: (key, id) => request(`/api/admin/catalogs/${key}/items/reactivate`, { method: 'POST', body: { id }, auth: true }),
  setStoreStatus: (id, activo) => request('/api/admin/stores/status', { method: 'POST', body: { id, activo }, auth: true }),
  updateStoreRow: (id, fields) => request(`/api/admin/stores/${id}`, { method: 'POST', body: fields, auth: true }),
  createStore: (fields) => request('/api/admin/stores/create', { method: 'POST', body: fields, auth: true }),
  /** Guardado de la edición avanzada: altas, ediciones y cambios de estatus en UNA transacción (todo o nada). */
  applyStoresBatch: (batch) => request('/api/admin/stores/batch', { method: 'POST', body: batch, auth: true }),
  /** Estado, formato, proveedor y subgerente que ofrece el formulario; piden permisos de TIENDAS, no de Catálogos. */
  getStoreFormOptions: () => request('/api/admin/stores/form-options', { auth: true }),
  getStoresTemplate,
  exportStores,
  importStoresFile,
  previewStoresFile,

  listProveedoresAdmin: () => request('/api/admin/proveedores', { auth: true }),
  listProveedoresEliminados: () => request('/api/admin/proveedores?ocultos=1', { auth: true }),
  saveProveedor: (payload) => request('/api/admin/proveedores', { method: 'POST', body: payload, auth: true }),
  // deshabilitar/habilitar: el proveedor SIGUE en la lista de admin, solo
  // pierde/recupera accesos. Distinto de ocultar/restaurar (eliminar de
  // verdad la lista, migracion 038) -- ver ProvidersPanel.jsx.
  deshabilitarProveedor: (id) => request('/api/admin/proveedores/delete', { method: 'POST', body: { id }, auth: true }),
  habilitarProveedor: (id) => request('/api/admin/proveedores/reactivate', { method: 'POST', body: { id }, auth: true }),
  hideProveedor: (id) => request('/api/admin/proveedores/hide', { method: 'POST', body: { id }, auth: true }),
  restoreProveedor: (id) => request('/api/admin/proveedores/restore', { method: 'POST', body: { id }, auth: true }),

  // Reporte de Drivers
  getDriverMotivos: () => request('/api/catalog/driver-motivos', { auth: true }),
  getDriverHorarios: () => request('/api/catalog/driver-horarios', { auth: true }),
  listDriverReports: (filters, { page = 0, pageSize = 20 } = {}) => request('/api/driver-reports/list', { method: 'POST', body: { filters, page, pageSize }, auth: true }),
  createDriverReport: (payload) => request('/api/driver-reports/create', { method: 'POST', body: payload, auth: true }),
  getDriverReportDetail: (id) => request(`/api/driver-reports/${id}`, { auth: true }),
  changeDriverReportEstado: (id, payload) => request(`/api/driver-reports/${id}/estado`, { method: 'POST', body: payload, auth: true }),
  exportDriverReports,
  getDriverReportStats: (filters) => request('/api/driver-reports/stats', { method: 'POST', body: { filters }, auth: true }),
};
